import type { VoidFunctionComponent } from 'react';

import { useReportLogoSettings } from '../../../../hooks/api/organization/useReportLogoSettings';
import useUpdateProjectReportCustomText from '../../../../hooks/api/projects/useUpdateProjectReportCustomText';
import useEditable from '../../../../hooks/useEditable';
import { nuc } from '../../../../utils';
import { FormGroup, LabeledSelect } from '../../../generic/Forms';
import { Description } from '../../../reportsv3/settings/Description';
import { Logo } from '../../../reportsv3/settings/Logo';
import Header from '../../../shared/settings/SettingsPageHeader';
import Paragraph from '../../../shared/settings/SettingsPageParagraph';
import Section from '../../../shared/settings/SettingsPageSection';
import { useProjectSettingsContext } from '../context';
import PageContent from '../shared/SettingsPageContent';
import type { SelectOption } from '../../../../types';

import { useProjectReportsSettings } from './useProjectReportsSettings';

const ProjectReportsSettingsContainer: VoidFunctionComponent = () => {
  const { project } = useProjectSettingsContext();

  const {
    fileUrl,
    setFile,
    unsetFile,
    handleImgError,
    onSave: onSaveOrganizationLogo,
    isSaveDisabled: isOrganizationLogoSaveDisabled,
    isLoading: isOrganizationLogoLoading,
  } = useReportLogoSettings();

  const {
    resolvedSettings,
    reportOptionSelectOptions,
    isLoading,
    isSaving,
    isSaveButtonDisabled,
    canEdit,
    onUpdate,
    onSave: onSaveProjectReports,
  } = useProjectReportsSettings();

  const {
    data: descriptionText,
    onUpdate: onUpdateDescription,
    isDirty: isDescriptionDirty,
    onSave: onSaveDescriptionState,
  } = useEditable<string>(project.reportCustomText ?? '');

  const { mutateAsync: updateCustomText, isPending: isCustomTextSaving } = useUpdateProjectReportCustomText();

  const isAnySaving = isOrganizationLogoLoading || isSaving || isCustomTextSaving;
  const isSaveButtonDisabledCombined = isOrganizationLogoSaveDisabled && isSaveButtonDisabled && !isDescriptionDirty;

  const onSaveAll = async () => {
    if (!isOrganizationLogoSaveDisabled) {
      await onSaveOrganizationLogo();
    }
    if (!isSaveButtonDisabled) {
      await onSaveProjectReports();
    }
    if (isDescriptionDirty && project.locator) {
      try {
        await updateCustomText({
          projectLocator: nuc(project.locator),
          reportCustomText: descriptionText,
        });
        onSaveDescriptionState();
      } catch {
        // useToastedMutation already displays the error toast.
        // The catch prevents an unhandled promise rejection.
      }
    }
  };

  return (
    <PageContent isLoading={isLoading}>
      <Header heading="Reports">
        <Header.SaveButton isLoading={isAnySaving} isDisabled={isSaveButtonDisabledCombined} onClick={onSaveAll} />
      </Header>
      <Logo
        fileUrl={fileUrl}
        setFile={setFile}
        unsetFile={unsetFile}
        handleImgError={handleImgError}
      />
      <Description
        value={descriptionText}
        onChange={onUpdateDescription}
        isDisabled={!canEdit || isAnySaving}
      />
      <Section>
        <Section.Left heading="Saved Options">
          <Section.Paragraph>Choose default options for each report type.</Section.Paragraph>
        </Section.Left>
        <Section.Right>
          <FormGroup>
            <div>
              <LabeledSelect<SelectOption<number>>
                label="Default options for Licensing reports"
                id="project-settings-default-saved-option-licensing-report"
                placeholder="No filter selected"
                onChange={(option) =>
                  onUpdate({
                    defaultSavedOptionLicensingReport: option?.value ?? null,
                  })}
                options={reportOptionSelectOptions}
                value={resolvedSettings.defaultSavedOptionLicensingReport}
                isDisabled={!canEdit || isAnySaving}
                isClearable
              />
              <Paragraph>This filter will be active by default when a user visits the report</Paragraph>
            </div>
            <div>
              <LabeledSelect<SelectOption<number>>
                label="Default options for SBOM reports"
                id="project-settings-default-saved-option-sbom"
                placeholder="No filter selected"
                onChange={(option) =>
                  onUpdate({
                    defaultSavedOptionSbom: option?.value ?? null,
                  })}
                options={reportOptionSelectOptions}
                value={resolvedSettings.defaultSavedOptionSbom}
                isDisabled={!canEdit || isAnySaving}
                isClearable
              />
              <Paragraph>This filter will be active by default when a user visits the report</Paragraph>
            </div>
          </FormGroup>
        </Section.Right>
      </Section>
    </PageContent>
  );
};

export default ProjectReportsSettingsContainer;
